import { ActionIcon, Button, Divider, Tooltip } from '@mantine/core';
import { Pencil, Trash2, Undo } from 'lucide-react';
import { useMemo, useState } from 'react';
import { validate } from 'uuid';

import { DeleteAppointment } from '@/components/AppointmentScheduler/delete-appointment.tsx';
import Icon from '@/components/Icon.tsx';
import OverlaySection from '@/components/OverlaySection.tsx';
import { TicketNumberPill } from '@/components/PatientModal/ticket-number-pill.tsx';
import PriorityMarker from '@/components/PriorityMarker.tsx';
import { AppointmentPill, getCollectionPillColor } from '@/components/appointment-pill.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import useCheckInData from '@/hooks/useCheckInData.ts';
import usePrismic from '@/hooks/usePrismic.tsx';
import { useTicketEditor } from '@/hooks/useTicketEditor.tsx';

import { PrismicDataType } from '@/models/PrismicHookTypes.ts';
import {
    Appointment,
    AppointmentBookStatus,
    CollectionActionScope,
    DailyAppointment,
    Priority,
} from '@/models/appointment/Appointment.ts';
import {
    APPOINTMENT_CANCEL_OPTION_TRANSLATIONS,
    CancelAppointmentOptions,
} from '@/models/appointment/CancelAppointmentOptions.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { usePatientModalStore } from '@/stores/patientModalStore.ts';

import {
    getAppointmentCancelReasonTranslation,
    isChainMode,
    isCheckedIn,
    isCheckedOut,
    isCheckinArrived,
    isCheckinPending,
    isPastAppointment,
    isSeriesMode,
    titleAfterId,
} from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

import { PatientAppointmentActions } from './patient-appointment-actions.tsx';

interface Props {
    appointment: DailyAppointment;
    onEdit: () => void;
    onDelete: (
        appointment: Appointment,
        scope: CollectionActionScope | undefined,
        cancelOption: CancelAppointmentOptions
    ) => void;
    undoNoShow: () => Promise<void>;
    chainFutureAppointmentCount?: number;
}

const DISABLED_EDIT_AND_CANCEL_TOOLTIP = 'Der Termin kann nicht mehr geändert werden.'; // The appointment can no longer be modified.
const UNDO_NO_SHOW_TOOLTIP = 'No-Show rückgängig machen'; // Undo no show

export const AppointmentDetails = ({
    appointment,
    onEdit,
    onDelete,
    undoNoShow,
    chainFutureAppointmentCount,
}: Props) => {
    const [showDelete, setShowDelete] = useState(false);
    const [showUndoNoShow, setShowUndoNoShow] = useState(false);
    const formattedDate = useMemo(() => dayjs(appointment.start).format('dddd, D MMMM YYYY'), [appointment]);
    const formattedTime = useMemo(() => dayjs(appointment.start).format('HH:mm'), [appointment]);

    // Check-in / out
    const { getAppointmentCheckinData } = useCheckInData();
    const appointmentCheckinData: UserAppointmentDto | undefined = getAppointmentCheckinData(appointment);

    const isMarkedNoShow = !!appointment.is_no_show;
    const isChain = isChainMode(appointment);
    const pastAppointment = isPastAppointment(dayjs(), appointment);
    const disableCancel =
        [
            AppointmentBookStatus.CANCELLED,
            AppointmentBookStatus.DECLINED,
            AppointmentBookStatus.PENDING_BOOKING,
        ].includes(appointment.status) ||
        isCheckedIn(appointmentCheckinData?.appointment?.checkin_status) ||
        isCheckinArrived(appointmentCheckinData?.appointment?.checkin_status) ||
        isCheckedOut(appointmentCheckinData?.appointment?.checkout_status) ||
        pastAppointment ||
        !!appointment.is_no_show;
    const disableEdit = disableCancel;

    const enableMarkNoShow =
        [
            AppointmentBookStatus.BOOKED,
            AppointmentBookStatus.COMPLETED, // Appointments are automatically moved to COMPLETED after a time
        ].includes(appointment.status) &&
        isCheckinPending(appointmentCheckinData?.appointment?.checkin_status) &&
        !appointment.is_no_show &&
        !isChain; // NO_SHOW is not available for chain appointments (chains always cancel all future)

    const { data: professionalProfiles } = usePrismic(PrismicDataType.PROFESSIONAL_PROFILE);
    const practitioner =
        getTextFromRichTextField(professionalProfiles?.get(appointment.professional.key)?.display_name) ??
        appointment.professional.key;

    const chainName = appointment.chain_configuration_id ? appointment.collection_data?.chain_name?.de : undefined;

    const handleUndoNoShow = async (): Promise<void> => {
        setShowUndoNoShow(false);
        await undoNoShow();
    };

    const handleDelete = (scope: CollectionActionScope | undefined, reason: CancelAppointmentOptions): void => {
        setShowDelete(false);
        onDelete(appointment, scope, reason);
    };

    const isTicketDisplayed = appointmentCheckinData?.number && !validate(appointmentCheckinData.number);
    const canEditTicket = Boolean(
        isTicketDisplayed &&
        appointment &&
        appointmentCheckinData &&
        !isCheckedOut(appointmentCheckinData?.appointment?.checkout_status)
    );
    const cancelReasonLabel = getAppointmentCancelReasonTranslation(appointment.cancel_reason);

    const { handleEditTicket } = useTicketEditor({
        appointment: appointment,
        checkinData: appointmentCheckinData!,
    });

    return (
        <OverlaySection className="relative overflow-hidden p-0">
            <PriorityMarker
                priority={appointment.priority ?? Priority.NORMAL}
                onClick={() =>
                    usePatientModalStore.setState((state) => ({
                        is_importance_opened: !state.is_importance_opened,
                    }))
                }
            />

            <div className="relative flex w-full flex-col gap-4 p-4">
                <div className="flex justify-between">
                    <div className="flex flex-col gap-1">
                        {chainName && <Paragraph className="text-lg font-semibold text-black">{chainName}</Paragraph>}
                        <div className="flex items-center gap-2">
                            {appointment?.collection_data && (
                                <AppointmentPill
                                    text={`${appointment.collection_data.position}/${appointment.collection_data.total_appointments}`}
                                    color={getCollectionPillColor(appointment)}
                                    variant="filled"
                                />
                            )}
                            <Paragraph className="text-lg text-slate-500">
                                {titleAfterId(appointment.name)} - {appointment.duration.value} Min
                            </Paragraph>
                        </div>
                        <Paragraph className="font-md">
                            {formattedDate} um {formattedTime} mit {practitioner}
                        </Paragraph>
                        {appointment.description && (
                            <Paragraph className="font-normal">{appointment.description}</Paragraph>
                        )}
                        {appointment.data.info && (
                            <Paragraph className="font-normal italic">{appointment.data.info}</Paragraph>
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        {isTicketDisplayed && appointmentCheckinData?.number && (
                            <TicketNumberPill
                                ticketNumber={appointmentCheckinData.number}
                                isEditable={canEditTicket}
                                onEdit={handleEditTicket}
                            />
                        )}
                        <div>
                            {isMarkedNoShow ? (
                                <Tooltip label={UNDO_NO_SHOW_TOOLTIP}>
                                    <ActionIcon
                                        variant="transparent"
                                        aria-label="undo-no-show-icon"
                                        onClick={() => setShowUndoNoShow(true)}
                                        className="bg-transparent text-black"
                                    >
                                        <Undo className="size-5 cursor-pointer" />
                                    </ActionIcon>
                                </Tooltip>
                            ) : (
                                <Tooltip
                                    label={DISABLED_EDIT_AND_CANCEL_TOOLTIP}
                                    disabled={!disableCancel || enableMarkNoShow}
                                >
                                    <ActionIcon
                                        disabled={(disableCancel && !enableMarkNoShow) || showDelete}
                                        variant="transparent"
                                        aria-label="delete-appointment-icon"
                                        onClick={() => setShowDelete(true)}
                                        className="bg-transparent" // otherwise it has a bg when disabled
                                    >
                                        <Icon
                                            icon={Trash2}
                                            color={
                                                (disableCancel && !enableMarkNoShow) || showDelete
                                                    ? 'text-slate-400'
                                                    : 'text-red-600 hover:text-red-500'
                                            }
                                        />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            {!isChain && (
                                <Tooltip label={DISABLED_EDIT_AND_CANCEL_TOOLTIP} disabled={!disableEdit}>
                                    <ActionIcon
                                        disabled={disableEdit}
                                        variant="transparent"
                                        aria-label="edit-appointment-icon"
                                        onClick={onEdit}
                                        className="bg-transparent" // otherwise it has a bg when disabled
                                    >
                                        <Icon
                                            icon={Pencil}
                                            color={disableEdit ? 'text-slate-400' : 'text-black'}
                                            className="cursor-pointer"
                                        />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </div>
                        <div />
                    </div>
                </div>
                {showDelete && (
                    <>
                        <Divider className="w-full" />
                        <DeleteAppointment
                            onlyNoShow={disableCancel && enableMarkNoShow}
                            onDelete={handleDelete}
                            onCancel={() => setShowDelete(false)}
                            isSeriesMode={isSeriesMode(appointment)}
                            isChainMode={isChain}
                            chainFutureAppointmentCount={chainFutureAppointmentCount}
                        />
                    </>
                )}
                {showUndoNoShow && (
                    <>
                        <Divider className="w-full" />
                        <Button onClick={handleUndoNoShow}>No-Show rückgängig machen</Button>
                    </>
                )}
                <Divider className="w-full" />
                {/* Show detail only in case the appointment is booked or completed */}
                {[AppointmentBookStatus.BOOKED, AppointmentBookStatus.COMPLETED].includes(appointment.status) &&
                    !appointment.is_no_show && (
                        <PatientAppointmentActions key={appointment.meta.updated_at} appointment={appointment} />
                    )}
                {(appointment.status === AppointmentBookStatus.CANCELLED || !!appointment.is_no_show) && (
                    <Paragraph>
                        {/* The appointment was cancelled */}
                        Der Termin wurde storniert
                        {cancelReasonLabel && `: ${cancelReasonLabel}`}
                        {appointment.is_no_show && `: ${APPOINTMENT_CANCEL_OPTION_TRANSLATIONS.NO_SHOW}`}
                    </Paragraph>
                )}
            </div>
        </OverlaySection>
    );
};
